/**
 * Image Utilities
 *
 * Utilities for validating, processing, and compressing image files.
 * Provides file validation, dimension extraction, and WebP compression.
 *
 * @module image-utils
 */

import {
  MAX_FILE_SIZE,
  formatBytes,
} from '@wds/bug-reporter-types/constants';

/** Image MIME types for preview detection */
const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];

/**
 * Check if a file is an image based on MIME type
 */
export function isImageFile(file: File | Blob): boolean {
  return IMAGE_MIME_TYPES.includes(file.type);
}

/**
 * Get file extension from filename
 * Returns uppercase extension without dot, or 'FILE' if unknown
 */
export function getFileExtension(filename: string): string {
  if (!filename) return 'FILE';
  const parts = filename.split('.');
  if (parts.length < 2) return 'FILE';
  const ext = parts.pop()?.toUpperCase() || 'FILE';
  return ext.length > 5 ? ext.substring(0, 5) : ext;
}

/**
 * Validation result for image file
 */
export interface ImageValidationResult {
  /** Whether the file is valid */
  valid: boolean;
  /** Error message if validation failed */
  error?: string;
  /** Human-readable file size */
  size?: string;
  /** Detected MIME type */
  mimeType?: string;
}

/**
 * Image dimensions
 */
export interface ImageDimensions {
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
}

/**
 * Validate file against size limits
 *
 * Checks file size (max 5MB). Returns detailed validation result.
 * Accepts any file type.
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 *
 * @example
 * ```typescript
 * const file = new File(['...'], 'document.pdf', { type: 'application/pdf' });
 * const result = validateImageFile(file);
 * if (!result.valid) {
 *   console.error(result.error); // "File too large (max 5 MB)"
 * }
 * ```
 */
export function validateImageFile(file: File): ImageValidationResult {
  try {
    // Check if file exists
    if (!file) {
      return {
        valid: false,
        error: 'No file provided',
      };
    }

    const mimeType = file.type || 'application/octet-stream';

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File too large (max ${formatBytes(MAX_FILE_SIZE)})`,
        size: formatBytes(file.size),
        mimeType,
      };
    }

    // File is valid (any type allowed)
    return {
      valid: true,
      size: formatBytes(file.size),
      mimeType,
    };
  } catch (error) {
    console.error('[ImageUtils] Validation failed:', error);
    return {
      valid: false,
      error: 'Failed to validate file',
    };
  }
}

/**
 * Get image dimensions from file
 *
 * Loads the image and extracts its width and height.
 * Uses browser's Image API to read file metadata.
 *
 * @param file - Image file to read
 * @returns Promise resolving to image dimensions
 * @throws {Error} If file cannot be loaded or is not a valid image
 *
 * @example
 * ```typescript
 * try {
 *   const dimensions = await getImageDimensions(file);
 *   console.log(`Image: ${dimensions.width}x${dimensions.height}`);
 * } catch (error) {
 *   console.error('Failed to get dimensions:', error);
 * }
 * ```
 */
export async function getImageDimensions(file: File): Promise<ImageDimensions> {
  try {
    // Validate input
    if (!file) {
      throw new Error('No file provided');
    }

    // Create object URL for file
    const objectUrl = URL.createObjectURL(file);

    // Load image
    const img = new Image();
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image file'));
    });

    img.src = objectUrl;
    await loadPromise;

    // Extract dimensions
    const dimensions: ImageDimensions = {
      width: img.width,
      height: img.height,
    };

    // Clean up object URL
    URL.revokeObjectURL(objectUrl);

    return dimensions;
  } catch (error) {
    console.error('[ImageUtils] Failed to get image dimensions:', error);
    throw new Error(
      error instanceof Error
        ? `Failed to get image dimensions: ${error.message}`
        : 'Failed to get image dimensions due to unknown error'
    );
  }
}

/**
 * Compress image file to WebP format
 *
 * Converts PNG/JPEG/GIF images to WebP at specified quality level.
 * Uses Canvas API for format conversion and compression.
 * Falls back to original format if WebP is not supported.
 *
 * @param file - Image file to compress
 * @param quality - Compression quality (0.0 to 1.0, default: 0.8 = 80%)
 * @returns Promise resolving to compressed blob
 * @throws {Error} If compression fails or file is invalid
 *
 * @example
 * ```typescript
 * try {
 *   const compressed = await compressImage(file, 0.8);
 *   console.log(`Original: ${file.size}, Compressed: ${compressed.size}`);
 * } catch (error) {
 *   console.error('Compression failed:', error);
 * }
 * ```
 */
export async function compressImage(
  file: File,
  quality: number = 0.8
): Promise<Blob> {
  try {
    // Validate input
    if (!file) {
      throw new Error('No file provided');
    }

    if (quality < 0 || quality > 1) {
      throw new Error('Quality must be between 0.0 and 1.0');
    }

    // Check WebP support
    const canvas = document.createElement('canvas');
    const webpSupport = canvas
      .toDataURL('image/webp')
      .startsWith('data:image/webp');

    if (!webpSupport) {
      console.warn('[ImageUtils] WebP not supported, returning original file');
      return file;
    }

    // Load image
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for compression'));
    });

    img.src = objectUrl;
    await loadPromise;

    // Create canvas with image dimensions
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw image to canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context for compression');
    }
    ctx.drawImage(img, 0, 0);

    // Convert to WebP blob
    const blobPromise = new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate compressed blob'));
          }
        },
        'image/webp',
        quality
      );
    });

    const compressed = await blobPromise;

    // Clean up object URL
    URL.revokeObjectURL(objectUrl);

    return compressed;
  } catch (error) {
    console.error('[ImageUtils] Compression failed:', error);
    throw new Error(
      error instanceof Error
        ? `Image compression failed: ${error.message}`
        : 'Image compression failed due to unknown error'
    );
  }
}

/**
 * Convert File or Blob to base64 data URL
 *
 * Utility function to convert image files to base64 encoding
 * for preview purposes or API submission.
 *
 * @param file - File or Blob to convert
 * @returns Promise resolving to base64 data URL
 * @throws {Error} If conversion fails
 *
 * @example
 * ```typescript
 * try {
 *   const base64 = await fileToBase64(file);
 *   console.log('Base64 length:', base64.length);
 * } catch (error) {
 *   console.error('Conversion failed:', error);
 * }
 * ```
 */
export async function fileToBase64(file: File | Blob): Promise<string> {
  try {
    if (!file) {
      throw new Error('No file provided');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (result && result.startsWith('data:')) {
          resolve(result);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error('[ImageUtils] Base64 conversion failed:', error);
    throw new Error(
      error instanceof Error
        ? `Failed to convert to base64: ${error.message}`
        : 'Failed to convert to base64 due to unknown error'
    );
  }
}
